import { useEffect } from "react";
import Layout from "@/components/Layout";
import Seo from "@/components/Seo";
import Eyebrow from "@/components/site/Eyebrow";
import { ACCENT, NAVY, CREAM } from "@/lib/siteTheme";
import heronIcon from "@/assets/design/blue-heron-icon.webp";

const steps = [
  { n: "1", title: "Quick intro call", desc: "We hop on a 30-minute call and talk through your business and goals." },
  { n: "2", title: "Honest assessment", desc: "I'll tell you what's actually possible, and what's not worth your money." },
  { n: "3", title: "A clear next step", desc: "If it's a fit, I'll send a simple proposal. If not, you'll walk away with useful advice anyway." },
];

const Book = () => {
  useEffect(() => {
    const SRC = "https://link.msgsndr.com/js/form_embed.js";
    if (document.querySelector(`script[src="${SRC}"]`)) return;
    const script = document.createElement("script");
    script.src = SRC;
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  return (
    <Layout background={NAVY}>
      <div style={{ fontFamily: "'Archivo', sans-serif", background: NAVY }} className="bh-book w-full antialiased">
        <Seo
          title="Book A Call | Blue Heron Marketing"
          description="Book a free 30-minute consultation with Blue Heron Marketing. No pressure, no sales pitch, just an honest conversation about your goals."
          path="/book"
        />

        {/* Hero */}
        <section className="relative overflow-hidden px-6 md:px-14 pt-[120px] pb-[88px] text-center" style={{ background: NAVY }}>
          <div className="absolute -right-[140px] -bottom-10 pointer-events-none" style={{ width: 560, maxWidth: "55vw", opacity: 0.06, transform: "scaleX(-1)" }}>
            <img src={heronIcon} alt="" width={500} height={366} className="w-full h-auto block brightness-0 invert" style={{ animation: "bhFloat 11s ease-in-out infinite" }} />
          </div>
          <div className="max-w-[1280px] mx-auto relative z-10">
            <div className="flex flex-col items-center mb-6">
              <div className="font-extrabold text-[13px] tracking-[0.16em] uppercase mb-3" style={{ color: "rgba(247,243,236,0.7)" }}>Book A Call</div>
              <div className="w-8 h-[3px] rounded-sm" style={{ background: ACCENT }} />
            </div>
            <h1 className="font-black leading-[0.98] tracking-[-0.02em] mb-6" style={{ color: CREAM, fontSize: "clamp(40px,6vw,76px)" }}>
              Book a free consultation.
            </h1>
            <p className="max-w-[560px] mx-auto leading-relaxed" style={{ color: "rgba(247,243,236,0.78)", fontSize: "clamp(17px,1.6vw,20px)" }}>
              30 minutes. No pressure, no sales pitch, just an honest conversation about your goals.
            </p>
          </div>
        </section>

        {/* Booking widget + info */}
        <section className="px-6 md:px-14 pb-[140px]" style={{ background: CREAM }}>
          <div className="max-w-[980px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-7 items-start relative z-[2]" style={{ marginTop: -56 }}>
            <div className="bg-white rounded-[28px] overflow-hidden flex flex-col" style={{ border: "1px solid rgba(14,40,70,.08)", boxShadow: "0 30px 60px rgba(14,40,70,.1)" }}>
              <div className="px-7 py-6 flex items-center gap-2.5" style={{ borderBottom: "1px solid rgba(14,40,70,.08)" }}>
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "rgba(14,40,70,.18)" }} />
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "rgba(14,40,70,.18)" }} />
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: "rgba(14,40,70,.18)" }} />
                <span className="ml-2 font-bold text-[13px]" style={{ color: "rgba(14,40,70,.5)" }}>Schedule a time</span>
              </div>
              <div className="p-2 pb-3">
                <iframe
                  id="9cAOM1tKe5aYEDHxV9x9_book"
                  src="https://api.leadconnectorhq.com/widget/booking/9cAOM1tKe5aYEDHxV9x9"
                  style={{ width: "100%", height: 1100, border: "none", overflow: "auto", display: "block" }}
                  scrolling="auto"
                  title="Book a call"
                />
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="rounded-3xl p-8" style={{ background: NAVY }}>
                <div className="font-bold text-xs tracking-[0.1em] uppercase mb-[22px]" style={{ color: "rgba(247,243,236,0.5)" }}>What to Expect</div>
                <div className="flex flex-col gap-[18px]">
                  {steps.map((step) => (
                    <div key={step.n} className="flex gap-3.5 items-start">
                      <span className="flex-none w-[26px] h-[26px] rounded-full font-extrabold text-[13px] flex items-center justify-center" style={{ background: ACCENT, color: NAVY }}>
                        {step.n}
                      </span>
                      <div>
                        <div className="font-extrabold text-[15.5px]" style={{ color: CREAM }}>{step.title}</div>
                        <div className="text-sm mt-0.5 leading-snug" style={{ color: "rgba(247,243,236,0.6)" }}>{step.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-3xl px-[30px] py-7" style={{ border: "1px solid rgba(14,40,70,.08)" }}>
                <div className="flex flex-col gap-3.5 mb-[22px]">
                  {["Free, no obligation", "No long-term contracts", "Talk directly with Luke"].map((item) => (
                    <div key={item} className="flex items-center gap-2.5">
                      <span className="font-extrabold text-base" style={{ color: ACCENT }}>✓</span>
                      <span className="font-bold text-[14.5px]" style={{ color: NAVY }}>{item}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-5" style={{ borderTop: "1px solid rgba(14,40,70,.08)" }}>
                  <div className="font-bold text-[13px] uppercase tracking-[0.06em] mb-1.5" style={{ color: "rgba(17,33,47,0.5)" }}>Prefer to call?</div>
                  <a href="tel:+18163704263" className="font-extrabold text-[19px] no-underline" style={{ color: NAVY }}>(816) 370-4263</a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .bh-book h1, .bh-book h2, .bh-book h3 {
          font-family: 'Archivo', sans-serif;
        }
        @keyframes bhFloat {
          0% { transform: translateY(0) rotate(-6deg); }
          50% { transform: translateY(-22px) rotate(-4deg); }
          100% { transform: translateY(0) rotate(-6deg); }
        }
      ` }} />
    </Layout>
  );
};

export default Book;
